import React from 'react'

import { t } from 'i18next'

import { getAccountId, isPublisher } from '~/helpers/token-info'
import { SpendBarChart } from '~/modules/retailMedia/components/BudgetConsumptionReport'
import { useImpressionShareEnabled } from '~/modules/retailMedia/hooks/useImpressionShareEnabled'
import useInView from '~/modules/retailMedia/hooks/useInView'
import {
  useDailySpend,
  isNoSpendDataError
} from '~/modules/retailMedia/hooks/useSpendReports'

import * as S from '../../styles'
import ChartBox from '../ChartBox'

type DateRange = { start_date: string; end_date: string }

export interface DailySpendSectionProps {
  campaignId: string
  dateRange: DateRange
  overviewReady: boolean
  campaignEndAt?: string
}

const DailySpendSection = ({
  campaignId,
  dateRange,
  overviewReady,
  campaignEndAt
}: DailySpendSectionProps) => {
  const [ref, isInView] = useInView()
  const publisherId = isPublisher() ? getAccountId() : null
  const impressionShareEnabled = useImpressionShareEnabled({ publisherId })
  const { data, isPending, isFetching, error, refetch } = useDailySpend(
    campaignId,
    dateRange,
    isInView && overviewReady,
    campaignEndAt
  )

  return (
    <S.Section ref={ref}>
      <S.SectionTitle>{t('rm:spendReport.section.dailySpend')}</S.SectionTitle>
      <ChartBox
        loading={!isInView || (isPending && !data && !error)}
        refreshing={isInView && isFetching && (!!data || !!error)}
      >
        {(data || error) && (
          <SpendBarChart
            {...(data ?? {})}
            showImpressionShare={
              impressionShareEnabled &&
              !!data?.impressionShareData?.some(value => value != null)
            }
            endLabel={
              campaignEndAt ? t('rm:spendReport.chart.endLine') : undefined
            }
            error={!!error}
            errorMessage={
              isNoSpendDataError(error)
                ? t('rm:spendReport.error.noSpendData')
                : undefined
            }
            onRetry={isNoSpendDataError(error) ? undefined : refetch}
          />
        )}
      </ChartBox>
    </S.Section>
  )
}

export default DailySpendSection
